

import { useContext } from 'react'
import { KTSVG } from '../../_metronic/helpers'
import { UsersListFilter } from './filtterOption'
import { ComponentRequest } from './componentLayout'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { UploadFileForm } from './upload/uploadFile'
import HeaderSectionCount, { usePermission } from './helper'
import { DatesFilter } from './dateFiltterOption'
import { DwonloadFilter } from './UserDownloadFilter'
import { DwonloadCsv } from './downloadCsv'
import { useQueryResponseCount, useQueryResponseLoading } from './response'
import { OrderStatusFilter } from './orderFilterOption'
import { DocFilter } from './docFilter'
import { DownloadDispatch } from '../master/fullfillment/new_dispatch/downloadDispatch'
import { FilterKycRequest } from './filterKycRequest'
import { useAuth } from '../modules/auth'
import { OfferFilter } from './offerFilter'
import { InvoiceFilter } from './invoiceFilter'
import { ExceptionFilter } from './exceptionFilter'
import { DateOptionDownload } from './dateOptionDownload'

/**
 * Toolbar Component
 *
 * Renders a dynamic toolbar with filters, download/upload buttons, status badges, and navigation links based on context and user permissions.
 *
 * Features:
 * - Displays various filters and action buttons based on comRequest context
 * - Shows status badges for uploads, processed items, calls, and balances
 * - Integrates with permission checks for conditional rendering
 * - Supports navigation links for multiple actions (user, mapping, dispatch, etc.)
 * - Handles loading states and custom filter components
 */

// Toolbar component for rendering various filter and action buttons
const Toolbar = () => {
    const { currentUser } = useAuth()
    const { comRequest } = useContext(ComponentRequest)
    const permissions = usePermission(comRequest?.module, currentUser)
    const count = useQueryResponseCount()
    const isLoading = useQueryResponseLoading()
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const projectName = searchParams.get('projectName');
    const dispatchModeName = searchParams.get('dispatchModeName');
    const statusName = searchParams.get('statusName');
    const subStatusName = searchParams.get('subStatusName');
    const handlePrint = () => {
        // Build URL with query params
        const url = `/admin/fullfillment/new_dispatch/print-labels?projectName=${encodeURIComponent(projectName)}&dispatchModeName=${encodeURIComponent(dispatchModeName)}&statusName=${encodeURIComponent(statusName)}&subStatusName=${encodeURIComponent(subStatusName)}`;
        // Navigate to print page
        navigate(url);
    };

    return (
        <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
            {/* Document filter button */}
            {(comRequest.docFilter && permissions.includes('edit')) &&
                <DocFilter />
            }
            {/* KYC/Document type stats badges */}
            {(comRequest.type === "kyc" || comRequest.type === "doc") &&
                <HeaderSectionCount />
            }
            {/* Lead stats badge */}
            {(comRequest.type === "lead") &&
                <div className='d-flex'>
                    <span style={{ background: '#F1FAFF' }} class="badge  p-5 me-4 fs-6 text-gray-700 fw-semibolder">Total {count?.label} Calls :
                        {isLoading ? <span class="spinner-grow spinner-grow-sm text-primary" role="status" aria-hidden="true" > </span > : <span className='text-danger fw-semibold'>{' '}{count?.total_count ? count?.total_count : 0} </span>}
                    </span>
                </div>
            }
            {/* User ledger balance badge */}
            {(comRequest.type === "user_ledger") &&
                <div className='d-flex'>
                    <span class="badge badge-light-danger p-5 me-4 fs-6 text-dark fw-semibolder">Current Balance :
                        {isLoading ?
                            <span class="spinner-grow spinner-grow-sm text-primary" role="status" aria-hidden="true" > </span>
                            :
                            <span className='text-dark fw-semibold'>{count?.currentBalance ? count?.currentBalance : 0}</span>
                        }
                    </span>
                </div>
            }
            {/* Offer filter dropdown */}
            {comRequest.offerFilter &&
                <OfferFilter />
            }
            {comRequest.printlabel &&
                <div
                    className={`btn btn-light-danger me-2 ${!subStatusName ? 'disabled' : ''}`}
                    onClick={handlePrint}
                    style={{ pointerEvents: subStatusName ? 'auto' : 'none', opacity: subStatusName ? 1 : 0.5 }}
                >
                    <i className="bi bi-printer me-1"></i> Print Label
                </div>
            }
            {/* Date filter dropdown */}
            {comRequest.date_filter &&
                <DatesFilter filter_option={comRequest?.filter_option || []} />
            }
            {/* Company filter dropdown (skip for dispatch – uses pill DispatchFilter in header) */}
            {comRequest.company_filter && comRequest.type === 'dispatch' &&
                <UsersListFilter />
            }
            {/* Download by user type button */}
            {(comRequest.by_user_type_download && permissions.includes('download')) &&
                <DwonloadFilter type={comRequest?.type} className="btn btn-light-dark me-3" />
            }
            {/* Order status filter dropdown */}
            {(comRequest.orderStatusFilter) &&
                <OrderStatusFilter className='' />
            }
            {/* Download CSV button */}
            {(comRequest.download && permissions.includes('download')) &&
                <DwonloadCsv type={comRequest?.type} rowId={comRequest?.rowId || null} className="btn btn-light-dark  me-3" />
            }
            {/* Courier mapping download button */}
            {(comRequest.corier_mapping_download && permissions.includes('download')) &&
                <DwonloadCsv type={'courier_mapping_download'} name="Courier Download" rowId={null} className="btn btn-light-dark  me-3" />
            }
            {/* Dispatch download button */}
            {(comRequest.dispatch_download && permissions.includes('download')) &&
                <DownloadDispatch />
            }
            {/* Upload link button */}

            {(comRequest.uploadLink && permissions.includes('upload')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.uploadLink} >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    {comRequest.uploadLabel ? comRequest.uploadLabel : comRequest.label}
                </Link>
            }
            {/* Quick SKU link button */}
            {(comRequest.quickUrl && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.quickUrl} >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    Quick SKU
                </Link>
            }
            {/* Catalogue offer upload button */}
            {(comRequest.catalogueOfferUpload && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.catalogueOfferUpload} >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    {comRequest.label} offer
                </Link>
            }
            {/* Project SKU upload button */}
            {/* {(comRequest.uploadProjectSku && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.uploadProjectSku} >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    Project Sku
                </Link>
            } */}
            {/* SKU Point upload button */}
            {/* {(comRequest.uploadPointLink && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.uploadPointLink} >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    SKU Point
                </Link>
            } */}
            {/* User address upload button */}
            {(comRequest.user_address_upload && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={'/apps/company-management/end_user/address-upload'} >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    Address
                </Link>
            }
            {/* Dispatch upload button */}
            {(comRequest.uploadDispatchLink && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.uploadDispatchLink} >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    Dispatch
                </Link>
            }
            {/* Courier assign upload button */}
            {(comRequest.uploadLinkCourier && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.uploadLinkCourier} >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    Courier Assign
                </Link>
            }
            {/* No service upload button */}
            {(comRequest.uploadNoLink && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.uploadNoLink} >
                    <KTSVG path='/media/icons/duotune/arrows/arr078.svg' className='svg-icon-2' />
                    No Service
                </Link>
            }
            {/* Create user type button */}
            {(comRequest.createUserTypeButton && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.createUserTypeButton} >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    User Type
                </Link>
            }
            {/* User mapping button */}
            {(comRequest.userMapping && permissions.includes('edit')) &&
                <Link className='btn btn-light-dark  me-2' to={comRequest.userMapping} >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    Mapping
                </Link>
            }
            {/* Add link button (write permission) */}
            {(comRequest.addLink && permissions.includes('write')) &&
                <Link className='btn btn-dark me-2 ' to={comRequest.addLink} >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    {comRequest.label}
                </Link>
            }
            {/* Add sub-link button (write permission) */}
            {(comRequest.addSubLink && permissions.includes('write')) &&
                <Link className='btn btn-primary me-2 ' to={comRequest.addSubLink} >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    {comRequest.sublabel}
                </Link>
            }
            {/* Upload file form button */}
            {(comRequest.upload && permissions.includes('upload')) &&
                <UploadFileForm className='' module_type={comRequest?.module_type} uploadApi={''} />
            }
            {/* Print file form button */}
            {(comRequest.print && permissions.includes('edit')) &&
                <UploadFileForm className='' module_type={comRequest?.module_type} uploadApi={''} />
            }
            {/* KYC request filter dropdown */}
            {comRequest.kycRequest_filter &&
                <FilterKycRequest />
            }
            {/* Invoice filter dropdown */}
            {comRequest.invoiceFilter &&
                <InvoiceFilter />
            }
            {/* Exception filter dropdown */}
            {comRequest.exceptionFilter &&
                <ExceptionFilter />
            }
            {/* Date option download button */}
            {(comRequest.date_option_download && permissions.includes('download')) &&
                <DateOptionDownload downloadApi={comRequest?.downloadApi} project_key={comRequest?.project_key} className="btn btn-light-dark  me-3" />
            }
        </div>
    )
}

export { Toolbar }
